import { costCenterLabel } from '@loot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type Department, type LegalEntity, type Person } from '../api.ts';
import { can, useMe } from '../auth.tsx';
import { changed, FormModal, FormSection } from '../components/FormModal.tsx';
import {
  ListToolbar,
  SearchBox,
  StatusFilter,
  type StatusFilterValue,
} from '../components/ListFilters.tsx';
import { MasterDataNote, useMasterDataReadOnly } from '../components/master-data.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  issueFor,
  Loading,
  PageHeader,
  Select,
  Table,
} from '../components/ui.tsx';
import { CostCenterPicker } from '../components/CostCenterPicker.tsx';
import { MasterDeleteButton } from '../components/MasterDelete.tsx';
import { DepartmentJobScopeForm } from './DepartmentJobScope.tsx';
import { InfoButton, RecordDetails } from './structure/RecordInfo.tsx';
import { filterRows } from './structure/filters.tsx';

type Form = {
  legalEntityId: string;
  code: string;
  name: string;
  costCenterId: string;
  managerUserId: string;
  substituteUserId: string;
  isActive: boolean;
  netsuiteCode: string;
};

function emptyForm(legalEntityId = ''): Form {
  return {
    legalEntityId,
    code: '',
    name: '',
    costCenterId: '',
    managerUserId: '',
    substituteUserId: '',
    isActive: true,
    netsuiteCode: '',
  };
}

function toForm(d: Department): Form {
  return {
    legalEntityId: d.legalEntityId ?? '',
    code: d.code,
    name: d.name,
    costCenterId: d.costCenterId ?? '',
    managerUserId: d.managerUserId ?? '',
    substituteUserId: d.substituteUserId ?? '',
    isActive: d.isActive,
    netsuiteCode: d.netsuiteCode ?? '',
  };
}

function toPayload(f: Form) {
  return {
    ...f,
    code: f.code.toUpperCase(),
    legalEntityId: f.legalEntityId || null,
    managerUserId: f.managerUserId || null,
    substituteUserId: f.substituteUserId || null,
    costCenterId: f.costCenterId || null,
    netsuiteCode: f.netsuiteCode || null,
  };
}

type Editing = { id: string | null; form: Form; initial: Form };

export function DepartmentsPage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const qc = useQueryClient();
  // Em FP&A › Configurações a tela só consulta: o cadastro é mantido em Sistema.
  const readOnly = useMasterDataReadOnly();
  const mayManage = can(me, 'core.department.manage');
  const canManage = mayManage && !readOnly;
  const [filter, setFilter] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilterValue>('active');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [jobScope, setJobScope] = useState<Department | null>(null);

  const entities = useQuery({
    queryKey: ['legal-entities'],
    queryFn: () => api.get<LegalEntity[]>('/core/legal-entities'),
    enabled: can(me, 'core.legal_entity.read'),
  });
  const list = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<Department[]>('/core/departments'),
  });
  const people = useQuery({
    queryKey: ['departments', 'people'],
    queryFn: () => api.get<Person[]>('/core/departments/people'),
    enabled: canManage,
  });

  const save = useMutation({
    mutationFn: (e: Editing) =>
      e.id
        ? api.put<Department>(`/core/departments/${e.id}`, toPayload(e.form))
        : api.post<Department>('/core/departments', toPayload(e.form)),
    onSuccess: async () => {
      setEditing(null);
      await qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });

  const open = (id: string | null, form: Form) => {
    save.reset();
    setEditing({ id, form, initial: form });
  };
  const update = (patch: Partial<Form>) =>
    setEditing((cur) => cur && { ...cur, form: { ...cur.form, ...patch } });
  const text =
    (k: 'code' | 'name' | 'managerUserId' | 'substituteUserId' | 'netsuiteCode') =>
    (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      update({ [k]: ev.target.value });

  const entityById = new Map(entities.data?.map((e) => [e.id, e]));
  // Departamento com o código antigo e sem centro ligado (migração 0244): aviso no formulário.
  const editingRow = editing?.id ? list.data?.find((d) => d.id === editing.id) : undefined;
  const legacyCode =
    editingRow?.costCenterUnlinked && !editing?.form.costCenterId
      ? editingRow.costCenterCode
      : null;
  const keep = new Set(editing?.initial.costCenterId ? [editing.initial.costCenterId] : []);
  const rows = filterRows(
    (list.data ?? []).filter(
      (d) =>
        !filter || (filter === 'corporate' ? d.legalEntityId == null : d.legalEntityId === filter),
    ),
    q,
    status,
    (d) => [
      d.code,
      d.name,
      d.costCenterCode,
      d.costCenterName,
      costCenterLabel(d.costCenterCode, d.costCenterName),
      d.netsuiteCode,
      d.managerName,
    ],
  );

  return (
    <>
      <PageHeader
        title={t('departments.title')}
        description={t('departments.description')}
        actions={
          canManage && !editing ? (
            <Button onClick={() => open(null, emptyForm(filter === 'corporate' ? '' : filter))}>
              {t('departments.new')}
            </Button>
          ) : null
        }
      />
      <MasterDataNote allowed={mayManage} />

      <FormModal
        open={!!editing}
        title={editing?.id ? t('departments.edit') : t('departments.new')}
        saveLabel={editing?.id ? undefined : t('departments.create')}
        dirty={!!editing && changed(editing.initial, editing.form)}
        saving={save.isPending}
        error={save.error}
        onSave={() => editing && save.mutate(editing)}
        onClose={() => setEditing(null)}
      >
        {editing && (
          <FormSection>
            <Field
              label={t('departments.legalEntity')}
              hint={editing.id ? t('departments.legalEntityLocked') : undefined}
              error={issueFor(save.error, 'legalEntityId')}
            >
              <Select
                id="dep-entity"
                disabled={!!editing.id}
                value={editing.form.legalEntityId}
                onChange={(ev) => update({ legalEntityId: ev.target.value })}
              >
                <option value="">{t('departments.corporate')}</option>
                {entities.data?.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.code} · {e.legalName}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={t('fields.code')}
              hint={t('departments.codeHint')}
              error={issueFor(save.error, 'code')}
              required
            >
              <Input id="dep-code" required value={editing.form.code} onChange={text('code')} />
            </Field>
            <Field label={t('fields.name')} error={issueFor(save.error, 'name')} required>
              <Input id="dep-name" required value={editing.form.name} onChange={text('name')} />
            </Field>
            <Field
              label={t('structure.fields.netsuiteCode')}
              hint={t('structure.netsuiteHint')}
              error={issueFor(save.error, 'netsuiteCode')}
            >
              <Input
                id="dep-netsuite"
                maxLength={100}
                value={editing.form.netsuiteCode}
                onChange={text('netsuiteCode')}
              />
            </Field>
            <Field label={t('departments.manager')} hint={t('departments.managerHint')}>
              <Select
                id="dep-manager"
                value={editing.form.managerUserId}
                onChange={text('managerUserId')}
              >
                <option value="">{t('common.none')}</option>
                {people.data?.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName} · {u.email}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={t('departments.substitute')}
              hint={t('departments.substituteHint')}
              error={issueFor(save.error, 'substituteUserId')}
            >
              <Select
                id="dep-substitute"
                value={editing.form.substituteUserId}
                onChange={text('substituteUserId')}
              >
                <option value="">{t('common.none')}</option>
                {people.data
                  ?.filter((u) => u.id !== editing.form.managerUserId)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.displayName} · {u.email}
                    </option>
                  ))}
              </Select>
            </Field>
            <div className="flex flex-col gap-1 sm:col-span-2">
              <span className="text-sm font-medium">{t('departments.costCenter')}</span>
              <p className="text-sm text-muted">{t('departments.costCenterHint')}</p>
              {legacyCode && (
                <p role="status" className="text-sm text-danger">
                  {t('departments.costCenterUnlinked', { code: legacyCode })}
                </p>
              )}
              <CostCenterPicker
                value={editing.form.costCenterId}
                onChange={(id) => update({ costCenterId: id })}
                legalEntityId={editing.form.legalEntityId || null}
                allowUnleveled
                keepIds={keep}
                label={t('departments.costCenter')}
              />
              {editing.form.costCenterId && (
                <Button
                  variant="secondary"
                  className="self-start"
                  onClick={() => update({ costCenterId: '' })}
                >
                  {t('departments.costCenterClear')}
                </Button>
              )}
            </div>
            <label className="flex items-center gap-2 self-end pb-2 text-sm">
              <input
                id="dep-active"
                type="checkbox"
                checked={editing.form.isActive}
                onChange={(ev) => update({ isActive: ev.target.checked })}
              />
              {t('fields.active')}
            </label>
          </FormSection>
        )}
      </FormModal>

      {jobScope && (
        <DepartmentJobScopeForm department={jobScope} onClose={() => setJobScope(null)} />
      )}

      <ListToolbar>
        <SearchBox value={q} onChange={setQ} placeholder={t('structure.searchPlaceholder')} />
        <StatusFilter value={status} onChange={setStatus} />
        <Select
          id="dep-filter"
          className="w-full sm:w-64"
          aria-label={t('departments.filterByEntity')}
          value={filter}
          onChange={(ev) => setFilter(ev.target.value)}
        >
          <option value="">{t('common.allCompanies')}</option>
          <option value="corporate">{t('departments.corporate')}</option>
          {entities.data?.map((e) => (
            <option key={e.id} value={e.id}>
              {e.code} · {e.legalName}
            </option>
          ))}
        </Select>
      </ListToolbar>

      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : (
        <Table
          head={[
            t('fields.code'),
            t('fields.name'),
            t('departments.legalEntity'),
            t('departments.manager'),
            t('departments.substitute'),
            t('fields.status'),
            '',
          ]}
        >
          {rows.length === 0 && <EmptyRow colSpan={7}>{t('departments.empty')}</EmptyRow>}
          {rows.map((d) => (
            <Fragment key={d.id}>
              <tr>
                <td className="font-mono">{d.code}</td>
                <td>
                  {d.name}
                  {d.costCenterCode && (
                    <div className="text-xs text-muted">
                      {t('departments.costCenter')}:{' '}
                      {costCenterLabel(d.costCenterCode, d.costCenterName)}
                    </div>
                  )}
                  {d.costCenterUnlinked && (
                    <div className="text-xs text-danger">
                      {t('departments.costCenterUnlinkedShort')}
                    </div>
                  )}
                  {d.netsuiteCode && (
                    <div className="text-xs text-muted">NetSuite: {d.netsuiteCode}</div>
                  )}
                </td>
                <td className="font-mono text-xs">
                  {d.legalEntityId
                    ? (d.legalEntityCode ?? entityById.get(d.legalEntityId)?.code ?? '—')
                    : t('departments.corporate')}
                </td>
                <td>{d.managerName ?? '—'}</td>
                <td>{d.substituteName ?? '—'}</td>
                <td>
                  {d.isActive ? (
                    <Badge tone="ok">{t('fields.active')}</Badge>
                  ) : (
                    <Badge tone="off">{t('fields.inactive')}</Badge>
                  )}
                </td>
                <td className="text-right whitespace-nowrap">
                  <span className="flex justify-end gap-2">
                    {d.canEdit && !readOnly && (
                      <Button variant="secondary" onClick={() => open(d.id, toForm(d))}>
                        {t('common.edit')}
                      </Button>
                    )}
                    {d.canEdit && !readOnly && (
                      <Button variant="secondary" onClick={() => setJobScope(d)}>
                        {t('departments.jobScope.button')}
                      </Button>
                    )}
                    <InfoButton
                      open={info === d.id}
                      onClick={() => setInfo(info === d.id ? null : d.id)}
                    />
                    <MasterDeleteButton table="core.department" id={d.id} compact />
                  </span>
                </td>
              </tr>
              {info === d.id && (
                <tr>
                  <td colSpan={7}>
                    <RecordDetails type="department" id={d.id} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </Table>
      )}
    </>
  );
}
